import React, { useState, useEffect } from 'react';
import { DeviceFamily, Brand, FramingSettings } from '../../types';
import { X, Save, Upload, Trash2, LayoutGrid, CheckCircle2, AlertCircle, Eye, EyeOff, Monitor, Tablet, Smartphone } from 'lucide-react';
import { storageService } from '../../services/StorageService';
import { useToast } from '../../context/ToastContext';
import { compressImage } from '../../utils/imageOptimizer';
import { ConfirmDialog } from './ConfirmDialog';

interface EditFamilyModalProps {
    family: DeviceFamily | null;
    brands: Brand[];
    isOpen: boolean;
    onClose: () => void;
    onSave: (id: string | undefined, data: Partial<DeviceFamily>) => Promise<void>;
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

export const EditFamilyModal: React.FC<EditFamilyModalProps> = ({ family, brands, isOpen, onClose, onSave }) => {
    const { showToast } = useToast();
    const [formData, setFormData] = useState<Partial<DeviceFamily>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);

    const isCreateMode = !family?.id;

    useEffect(() => {
        if (isOpen) {
            if (family) {
                setFormData({
                    ...family,
                    is_active: family.is_active ?? true,
                    framing_settings: family.framing_settings || {
                        mobile: { scale: 1, x: 50, y: 50 },
                        tablet: { scale: 1, x: 50, y: 50 },
                        desktop: { scale: 1, x: 50, y: 50 }
                    }
                });
                setPreviewUrl(family.image_url || null);
            } else {
                setFormData({
                    is_active: true,
                    display_order: 0,
                    brand_id: brands.length > 0 ? brands[0].id : '',
                    framing_settings: {
                        mobile: { scale: 1, x: 50, y: 50 },
                        tablet: { scale: 1, x: 50, y: 50 },
                        desktop: { scale: 1, x: 50, y: 50 }
                    }
                });
                setPreviewUrl(null);
            }
            setImageFile(null);
        }
    }, [family, isOpen, brands]);

    // Cleanup object urls
    useEffect(() => {
        return () => {
            if (imageFile && previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl, imageFile]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name?.trim()) {
            showToast('El nombre de la familia es obligatorio', 'error');
            return;
        }
        if (!formData.brand_id) {
            showToast('Debe seleccionar una marca', 'error');
            return;
        }

        setIsSaving(true);
        try {
            let imageUrl = formData.image_url;

            if (imageFile) {
                const processedFile = await compressImage(imageFile, { maxWidth: 1200, quality: 0.8 });
                imageUrl = await storageService.uploadImage(processedFile, 'families', 'banners');
            }

            const dataToSave = {
                ...formData,
                image_url: imageUrl,
                slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
            };

            await onSave(family?.id, dataToSave);
            showToast('Familia guardada correctamente', 'success');
            onClose();
        } catch (error: any) {
            console.error('Error saving family:', error);
            showToast(error.message || 'Error al guardar la familia', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showToast('Solo se permiten imágenes', 'error');
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        setImageFile(file);
    };

    const handleFramingChange = (field: keyof FramingSettings['mobile'], value: number) => {
        setFormData(prev => ({
            ...prev,
            framing_settings: {
                ...prev.framing_settings!,
                mobile: { ...prev.framing_settings!.mobile, [field]: value },
                tablet: { ...prev.framing_settings!.tablet, [field]: value },
                desktop: { ...prev.framing_settings!.desktop, [field]: value }
            }
        }));
    };

    const currentFraming = formData.framing_settings?.mobile || { scale: 1, x: 50, y: 50 };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div
                className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-4xl shadow-2xl animate-in fade-in zoom-in duration-200 overflow-hidden flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-zinc-800 bg-zinc-900 sticky top-0 z-10">
                    <h3 className="text-xl font-display font-bold uppercase italic text-white flex items-center gap-2">
                        {isCreateMode ? (
                            <span className="text-white">NUEVA FAMILIA</span>
                        ) : (
                            <>
                                EDITAR <span className="text-white">{family?.name}</span>
                            </>
                        )}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-zinc-800 rounded-full text-zinc-400 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col md:flex-row">
                    {/* LEFT: PREVIEW & FRAMING */}
                    <div className="w-full md:w-1/2 p-4 md:p-6 border-r border-zinc-800 bg-black/20 space-y-4 md:space-y-6 shrink-0 flex flex-col">
                        <div className="flex flex-col gap-3">
                            <label className="block text-[9px] font-bold uppercase text-zinc-500 tracking-wider">PREVISUALIZACIÓN DE BANNER</label>

                            <div className="relative aspect-[4/5] h-[280px] md:h-[340px] w-auto mx-auto bg-zinc-950 rounded-lg overflow-hidden border border-zinc-800 shadow-2xl shrink-0">
                                {previewUrl ? (
                                    <div className="w-full h-full relative overflow-hidden">
                                        <img
                                            src={previewUrl}
                                            className="absolute transition-all duration-300 ease-out"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transform: `scale(${currentFraming.scale})`,
                                                objectPosition: `${currentFraming.x}% ${currentFraming.y}%`
                                            }}
                                            alt="Preview"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-zinc-600 p-8">
                                        <Upload size={32} className="mb-3 opacity-20" />
                                        <p className="text-[9px] font-bold uppercase tracking-widest text-center">Sube una imagen</p>
                                    </div>
                                )}
                            </div>

                            {previewUrl && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-zinc-900/50 p-3 rounded-lg border border-zinc-800">
                                    <div className="space-y-1.5" title="Doble clic para resetear Zoom">
                                        <label className="block text-[8px] font-bold uppercase text-zinc-500">Zoom ({currentFraming.scale.toFixed(1)}x)</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="3"
                                            step="0.1"
                                            value={currentFraming.scale}
                                            onChange={(e) => handleFramingChange('scale', parseFloat(e.target.value))}
                                            onDoubleClick={() => handleFramingChange('scale', 1)}
                                            className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                    <div className="space-y-1.5" title="Doble clic para resetear Posición X">
                                        <label className="block text-[8px] font-bold uppercase text-zinc-500">Pos. X ({Math.round(currentFraming.x)}%)</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={currentFraming.x}
                                            onChange={(e) => handleFramingChange('x', parseInt(e.target.value))}
                                            onDoubleClick={() => handleFramingChange('x', 50)}
                                            className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                    <div className="space-y-1.5" title="Doble clic para resetear Posición Y">
                                        <label className="block text-[8px] font-bold uppercase text-zinc-500">Pos. Y ({Math.round(currentFraming.y)}%)</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={currentFraming.y}
                                            onChange={(e) => handleFramingChange('y', parseInt(e.target.value))}
                                            onDoubleClick={() => handleFramingChange('y', 50)}
                                            className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: FORM DATA */}
                    <form onSubmit={handleSubmit} className="w-full md:w-1/2 p-4 md:p-6 space-y-4 md:space-y-6 bg-zinc-900/20">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1.5 tracking-wider">Marca</label>
                                <select
                                    value={formData.brand_id || ''}
                                    onChange={e => setFormData({ ...formData, brand_id: e.target.value })}
                                    className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-sm text-white rounded transition-colors"
                                >
                                    <option value="" disabled>Seleccionar Marca</option>
                                    {brands.map(brand => (
                                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1.5 tracking-wider">Nombre Visual</label>
                                <input
                                    type="text"
                                    value={formData.name || ''}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-sm text-white rounded font-bold transition-colors"
                                    placeholder="Ej. iPhone 15 Series"
                                />
                            </div>

                            <div className="relative">
                                <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1.5 tracking-wider">Subir Imagen</label>
                                <div className="group relative border border-zinc-800 rounded p-3 bg-black hover:border-zinc-600 transition-colors cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    />
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-zinc-900 rounded border border-zinc-800 group-hover:bg-zinc-800 transition-colors">
                                            <Upload size={16} className="text-zinc-400 group-hover:text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[10px] text-zinc-300 font-bold uppercase truncate">
                                                {imageFile ? imageFile.name : 'Seleccionar Archivo'}
                                            </p>
                                            <p className="text-[9px] text-zinc-500 uppercase tracking-tight">Cualquier resolución (Recomendada 4:5)</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1.5 tracking-wider">Orden</label>
                                    <input
                                        type="number"
                                        value={formData.display_order ?? 0}
                                        onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                        className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-sm text-white rounded transition-colors"
                                    />
                                </div>

                                <div className="flex items-end">
                                    <label className={`
                                        w-full flex items-center justify-between p-3 rounded border cursor-pointer transition-all select-none h-[46px]
                                        ${formData.is_active ? 'bg-white/10 border-white/50' : 'bg-black border-zinc-800 hover:border-zinc-700'}
                                    `}>
                                        <span className={`text-[10px] font-bold uppercase flex items-center gap-2 ${formData.is_active ? 'text-white' : 'text-zinc-500'}`}>
                                            {formData.is_active ? 'Visible' : 'Oculto'}
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active || false}
                                            onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                            className="hidden"
                                        />
                                        <div className={`
                                            w-6 h-3 rounded-full flex items-center p-0.5 transition-colors duration-300
                                            ${formData.is_active ? 'bg-white' : 'bg-zinc-800'}
                                        `}>
                                            <div className={`
                                                w-2 h-2 rounded-full bg-black shadow-sm transition-transform duration-300
                                                ${formData.is_active ? 'translate-x-3' : 'translate-x-0'}
                                            `} />
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="p-4 md:p-6 border-t border-zinc-800 bg-zinc-950 sticky bottom-0 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 hover:text-white transition-all text-[10px] font-bold uppercase tracking-widest rounded"
                        disabled={isSaving}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="flex-[2] bg-white hover:bg-zinc-100 text-black font-bold py-3 text-[10px] uppercase rounded flex justify-center items-center gap-2 transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)] disabled:opacity-50"
                    >
                        {isSaving ? (
                            <>
                                <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                Guardar Cambios
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
